<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'next/dialog/Dialog.vue';
import EventDefinitionForm from '../EventDefinitionForm.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const definition = ref(null);
const dialogRef = ref(null);
const isOpen = ref(false);
const formRef = ref(null);
const uiFlags = useMapGetter('wgptEventDefinitions/getUIFlags');

const onClose = () => {
  isOpen.value = false;
  definition.value = null;
  emit('close');
};

const open = targetDefinition => {
  definition.value = { ...targetDefinition };
  isOpen.value = true;
  nextTick(() => dialogRef.value?.open());
};

const close = () => {
  dialogRef.value?.close();
  onClose();
};

const handleSubmit = async () => {
  if (!formRef.value) return;
  const isValid = await formRef.value.validate();
  if (!isValid) return;

  try {
    await store.dispatch('wgptEventDefinitions/update', {
      id: definition.value.id,
      ...formRef.value.state,
    });
    useAlert(t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.UPDATE_SUCCESS'));
    close();
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.UPDATE_ERROR')
    );
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.UPDATE_TITLE')"
    :confirm-button-label="
      t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.UPDATE')
    "
    :cancel-button-label="
      t('WGPT_CUSTOM_REPORTS.EVENT_DEFINITIONS.FORM.CANCEL')
    "
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    width="3xl"
    overflow-y-auto
    @confirm="handleSubmit"
    @close="onClose"
  >
    <EventDefinitionForm v-if="isOpen" ref="formRef" :definition="definition" />
  </Dialog>
</template>
